import { jsx as _jsx } from "react/jsx-runtime";
import { useSession } from '@side/next-auth/react';
import { jwtDecode } from 'jwt-decode';
import { usePathname, useSearchParams } from 'next/navigation';
import Script from 'next/script';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { close, open, setConversationFields, setZIndex, } from '../api/messenger';
import { CONVERSATION_FIELD_MAP } from '../constants';
import { messenger } from '../index';
import { isProd } from '../utils';
const REFRESH_WINDOW_IN_SECONDS = 60 * 60; // 1 hour
const FETCH_RETRY_COOLDOWN = 60 * 5; // 5 minutes
// These values are picked below using process.env.NEXT_PUBLIC_APP_ENV and any other
// environment will be undefined leading to the messenger not loading.
const MESSENGER_KEY_MAP = {
    prod: '0055a22d-e9fa-4d80-9f46-acb7cb0f3a92',
    stage: 'e5a40eb6-f524-403e-afa4-a75809ee3413',
};
const timeLeftToExpire = (decodedToken) => {
    if (!decodedToken.exp) {
        console.error('Zendesk API - Error token missing exp');
        return;
    }
    return decodedToken.exp - Date.now() / 1000;
};
function login(jwt) {
    try {
        window.zE?.('messenger', 'loginUser', (callback) => {
            console.debug('Zendesk API - Invoking Zendesk login callback');
            callback(jwt);
        }, (error) => {
            if (error) {
                const { type, reason, message } = error;
                console.error('Zendesk API - Login failed', {
                    type,
                    reason,
                    message,
                });
                return;
            }
            console.debug('Zendesk API - User logged in successfully');
        });
    }
    catch (err) {
        console.error('Zendesk API - Error logging User into messenger', err);
    }
}
function logout() {
    try {
        window.zE?.('messenger', 'logoutUser');
    }
    catch (err) {
        console.error('Zendesk API - Error logging user out of messenger', err);
    }
}
/**
 * Initializes the Zendesk script and uses the session token to preset the Name
 * and Email Zendesk Conversation fields. Takes an optional messengerKey if a
 * messenger other than the default is desired.
 * @returns Zendesk Web SDK code
 */
export function SetupZendesk({ messengerKey, onReady, }) {
    const env = process.env.NEXT_PUBLIC_APP_ENV;
    const session = useSession();
    const oktaJwt = session.data?.accessToken;
    const user = session?.data?.user;
    const status = session?.status;
    const { displayName, loginEmail, firstName, lastName } = user ?? {};
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const lastFetchAttemptRef = useRef(0);
    const previousStatusRef = useRef(status);
    const [jwt, setJwt] = useState(null);
    const [isFetching, setIsFetching] = useState(false);
    const startChatParam = searchParams.get('startChat');
    const startAChatParam = searchParams.get('startAChat');
    const openChat = useMemo(() => [startChatParam, startAChatParam].includes('true'), [startChatParam, startAChatParam]);
    const closeChat = useMemo(() => [startChatParam, startAChatParam].includes('false'), [startChatParam, startAChatParam]);
    const [ready, setReady] = useState(!!window.zE);
    const fetchJwt = useCallback(async () => {
        try {
            if (isFetching || !oktaJwt) {
                return;
            }
            const decodedOktaJwt = jwtDecode(oktaJwt);
            if (decodedOktaJwt.exp && decodedOktaJwt.exp < Date.now() / 1000) {
                return;
            }
            const now = Date.now() / 1000;
            if (now - lastFetchAttemptRef.current < FETCH_RETRY_COOLDOWN) {
                console.warn('Zendesk API - Zendesk JWT fetch skipped (cooldown)');
                return;
            }
            setIsFetching(true);
            lastFetchAttemptRef.current = now;
            const res = await fetch(`${process.env.NEXT_PUBLIC_BASE_PATH}/api/proxies/identity/auth/zendesk/token`, {
                method: 'POST',
                headers: {
                    authorization: `Bearer ${oktaJwt}`,
                },
            });
            if (!res.ok) {
                throw new Error(`Zendesk token fetch failed with status ${res.status}`);
            }
            const { token } = await res.json();
            setJwt(token);
        }
        catch (err) {
            // "Load failed" appears if there is a loss of network or user navigates away from page (especially on iOS)
            // "NetworkError when attempting to fetch" appears if requests are blocked (such as by extensions - especially in Firefox)
            if (err instanceof Error &&
                (err.message === 'Load failed' ||
                    err.message.includes('NetworkError when attempting to fetch'))) {
                console.debug(`Zendesk API - Zendesk JWT fetch skipped (${err.message})`);
            }
            else {
                console.error('Zendesk API - Failed to fetch Zendesk JWT:', err);
            }
        }
        finally {
            setIsFetching(false);
        }
    }, [isFetching, oktaJwt]);
    // Reset cooldown if token changes
    useEffect(() => {
        if (oktaJwt) {
            lastFetchAttemptRef.current = 0;
        }
    }, [oktaJwt]);
    // Initial fetch when no jwt exists
    useEffect(() => {
        if (status === 'authenticated' && ready && !jwt && !isFetching) {
            fetchJwt();
        }
    }, [status, ready, jwt, isFetching, fetchJwt]);
    // Handle logout by tracking previous auth status
    useEffect(() => {
        if (!window.zE ||
            !ready ||
            !['authenticated', 'unauthenticated'].includes(status)) {
            return;
        }
        if (previousStatusRef.current === 'authenticated' &&
            status === 'unauthenticated') {
            console.debug('Zendesk API - Logging out of Zendesk');
            logout();
        }
        previousStatusRef.current = status;
    }, [status, ready]);
    // Handle route changes
    // biome-ignore lint/correctness/useExhaustiveDependencies: using pathname to trigger on route change
    useEffect(() => {
        // Update the page URL on every route change
        messenger.setPageUrl(window.location.href);
        // Check JWT expiration
        if (!jwt) {
            return;
        }
        const decodedToken = jwtDecode(jwt);
        const timeLeft = timeLeftToExpire(decodedToken);
        if (timeLeft && timeLeft < REFRESH_WINDOW_IN_SECONDS && !isFetching) {
            fetchJwt(); // safe to use because it's defined outside
        }
    }, [pathname, fetchJwt, jwt]);
    // When JWT is updated, login to Zendesk Messenger
    useEffect(() => {
        if (!jwt || !window.zE || !ready || status !== 'authenticated') {
            return;
        }
        console.debug('Zendesk API - Logging into Zendesk with new JWT');
        login(jwt);
    }, [jwt, ready, status]);
    // Refresh JWT before expiration using timeout
    useEffect(() => {
        if (!window.zE || !ready || !jwt) {
            return;
        }
        const decodedToken = jwtDecode(jwt);
        const timeLeft = timeLeftToExpire(decodedToken);
        if (timeLeft === undefined) {
            // Not retrying during this error because it means the JWT provider,
            // identity-service, is in a bad state
            return;
        }
        if (timeLeft <= REFRESH_WINDOW_IN_SECONDS && !isFetching) {
            fetchJwt(); // refresh immediately if almost expired
            return;
        }
        const timeoutId = setTimeout(() => {
            fetchJwt(); // refresh 1 minute before expiry
        }, Math.max((timeLeft - REFRESH_WINDOW_IN_SECONDS) * 1000, 0));
        return () => clearTimeout(timeoutId);
    }, [jwt, fetchJwt, isFetching, ready]);
    useEffect(() => {
        try {
            if (!window.zE || !ready) {
                return;
            }
            setZIndex(1199);
            if (openChat) {
                open();
            }
            else if (closeChat) {
                // The else is to ensure that if one param is 'true' and one param is
                // 'false' we won't open and close and default to open in that instance.
                close();
            }
            // Display name is nullable and not always populated. First Name and Last
            // Name are required and exist if a user exists.
            if (user) {
                const name = displayName ?? `${firstName} ${lastName}`;
                if (!isProd()) {
                    console.debug('Zendesk API - Skipping set of user name on non prod env', name);
                    return;
                }
                setConversationFields(CONVERSATION_FIELD_MAP.USER_NAME, name);
            }
            if (loginEmail) {
                if (!isProd()) {
                    console.debug('Zendesk API - Skipping set of user email on non prod env', loginEmail);
                    return;
                }
                setConversationFields(CONVERSATION_FIELD_MAP.USER_EMAIL, loginEmail);
            }
        }
        catch (err) {
            const errorMessage = 'Cannot setup default Zendesk conversation fields';
            console.warn(`Zendesk API - ${errorMessage}`, err);
            throw new Error(errorMessage, { cause: err });
        }
    }, [
        firstName,
        lastName,
        openChat,
        displayName,
        loginEmail,
        closeChat,
        user,
        ready,
    ]);
    if (window.Cypress || !env) {
        return null;
    }
    messengerKey ??= MESSENGER_KEY_MAP[env];
    if (!messengerKey) {
        return null;
    }
    // Only download the snippet if the user is authenticated
    return user ? (
    // biome-ignore lint/nursery/useUniqueElementIds: script from zendesk
    _jsx(Script, { id: "ze-snippet", src: `https://static.zdassets.com/ekr/snippet.js?key=${messengerKey}`, onError: (err) => {
            console.error(`Zendesk API - Error loading Zendesk script: ${err.message}`, err);
        }, onReady: () => {
            setReady(true);
            onReady?.();
        } })) : null;
}
